import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { usePersistFn } from '@euler/utils/hooks';
import Color from 'color';
import { memo, ReactNode } from 'react';
import {
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'styled-components';

type Props<T> = {
  item: T;
  style?: StyleProp<ViewStyle>;
  name: string;
  title?: string;
  qty: number;
  hideQty?: boolean;
  coverUrl?: string;
  showBadge?: boolean;
  removable?: boolean;
  adopted: boolean | undefined;
  author?: { name: string };
  adopter?: { name: string };
  closer?: { name: string };
  onEditCloser?: (item: T) => void;
  onRemove?: (item: T) => void;
  onPress?: (item: T) => void;
};

function ProductItemCellUnmemo<T>(props: Props<T>) {
  const {
    item,
    name,
    title,
    coverUrl,
    style,
    showBadge,
    removable,
    adopted,
    qty,
    hideQty,
    author,
    adopter,
    closer,
    onEditCloser,
    onRemove,
    onPress,
  } = props;
  const { rpx } = useResponsiveContext();

  const {
    colors: {
      status: { danger },
    },
  } = useTheme();

  const onRemovePress = usePersistFn(() => {
    onRemove?.(item);
  });

  const handlePress = usePersistFn(() => {
    onPress?.(item);
  });

  const onEditCloserPress = usePersistFn(() => {
    onEditCloser?.(item);
  });

  const hasIdentityFooter =
    author != null || adopter != null || closer != null || onEditCloser != null;

  const bgColor = adopted
    ? 'rgb(43 154 76)'
    : adopted === false
    ? '#777'
    : 'rgb(34, 91, 160)';

  const badgeBgColor = adopted
    ? '#126029'
    : adopted === false
    ? '#333'
    : 'rgb(0, 15, 105)';

  return (
    <View style={style}>
      <TouchableOpacity
        onPress={handlePress}
        css={`
          background-color: ${bgColor};
          border-radius: ${rpx(5)}px;
          overflow: hidden;
        `}
      >
        <StackLayout
          direction="row-reverse"
          justifyContent="space-between"
          alignItems="stretch"
        >
          <View
            css={`
              flex: 1;
              padding: ${rpx(8)}px ${rpx(10)}px;
            `}
          >
            <Text style={{ marginRight: rpx(40) }}>
              <Typography.Label size={rpx(15)} bold color="#fff">
                {`${name}`}
              </Typography.Label>
              {adopted && !hideQty ? (
                <Text
                  style={{
                    fontStyle: 'italic',
                    fontWeight: 'bold',
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontSize: rpx(18),
                  }}
                >{`  x${qty}`}</Text>
              ) : undefined}
            </Text>
            <Typography.Label size={rpx(12)} color="rgba(255, 255, 255, 0.7)">
              {title ?? '暂无亮点介绍'}
            </Typography.Label>
          </View>
          <View
            css={`
              width: ${rpx(80)}px;
              min-height: ${rpx(80)}px;
              justify-content: center;
              align-items: center;
              flex-grow: 0;
              flex-shrink: 0;
              background-color: ${Color(bgColor).darken(0.05).rgb().toString()};
            `}
          >
            <Img
              uri={coverUrl}
              css={`
                width: ${rpx(64)}px;
                height: ${rpx(64)}px;
                border-radius: ${rpx(8)}px;
                overflow: hidden;
              `}
            />
          </View>
        </StackLayout>
        {showBadge && (
          <Center
            css={`
              position: absolute;
              right: 0;
              top: 0;
              padding: ${rpx(1)}px ${rpx(10)}px;
              border-top-right-radius: ${rpx(5)}px;
              border-bottom-left-radius: ${rpx(5)}px;
              background-color: ${badgeBgColor};
            `}
          >
            <Typography.Label
              color={'rgba(255, 255, 255, 0.85)'}
              regular
              size={rpx(7)}
            >
              产品介绍
            </Typography.Label>
          </Center>
        )}
        {hasIdentityFooter && (
          <View
            css={`
              flex-direction: row;
              align-items: center;
              padding: ${rpx(8)}px ${rpx(10)}px;
              border-top-width: 1px;
              border-top-color: rgba(255, 255, 255, 0.18);
            `}
          >
            <IdentityPair label="推荐人" value={author?.name} rpx={rpx} />
            <IdentityPair label="施工员" value={adopter?.name} rpx={rpx} />
            <IdentityPair
              label="成交人"
              value={closer?.name}
              rpx={rpx}
              trailing={
                onEditCloser ? (
                  <TouchableOpacity
                    onPress={onEditCloserPress}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    css={`
                      margin-left: ${rpx(4)}px;
                    `}
                  >
                    <Icon
                      name="pencil"
                      size={rpx(12)}
                      color="rgba(255, 255, 255, 0.85)"
                    />
                  </TouchableOpacity>
                ) : undefined
              }
            />
          </View>
        )}
      </TouchableOpacity>
      {removable && (
        <TouchableOpacity
          onPress={onRemovePress}
          css={`
            position: absolute;
            right: ${rpx(-5)}px;
            top: ${rpx(-5)}px;
            width: ${rpx(14)}px;
            height: ${rpx(14)}px;
            border-radius: ${rpx(8)}px;
            background-color: ${danger};
            align-items: center;
            justify-content: center;
          `}
        >
          <Icon name="close" size={rpx(10)} color="#fff" />
        </TouchableOpacity>
      )}
    </View>
  );
}

export const ProductItemCell = memo(
  ProductItemCellUnmemo,
) as typeof ProductItemCellUnmemo;

const IdentityPair = ({
  label,
  value,
  rpx,
  trailing,
}: {
  label: string;
  value?: string;
  rpx: (n: number) => number;
  trailing?: ReactNode;
}) => {
  return (
    <View
      css={`
        flex: 1;
        flex-direction: row;
        align-items: center;
      `}
    >
      <Typography.Label
        size={rpx(11)}
        color="rgba(255, 255, 255, 0.6)"
        style={{ marginRight: rpx(4) }}
      >
        {label}
      </Typography.Label>
      <Typography.Label
        size={rpx(12)}
        color={value ? '#fff' : 'rgba(255, 255, 255, 0.6)'}
      >
        {value ?? '--'}
      </Typography.Label>
      {trailing}
    </View>
  );
};
